"use strict";

import classNames from "classnames";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import React, {createRef} from "react";
import {
  FiAlertTriangle,
  FiCamera,
  FiCode,
  FiCopy,
  FiFileText,
  FiInfo,
  FiMoreVertical,
  FiPlus,
  FiTrash,
} from "react-icons/fi";
import {HiMenuAlt4} from "react-icons/hi";

import {Alert} from "@/components/alert/Alert.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Dropdown} from "@/components/dropdown/Dropdown.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {TooltipWrapper} from "@/components/overlay/TooltipWrapper.tsx";
import {StepDetailsModalButton} from "@/components/transaction/StepDetailsModalButton.tsx";
import {StepDetailsModalDropdownIcon} from "@/components/transaction/StepDetailsModalDropdownIcon.js";
import {StepRequestsWaterfallModal} from "@/components/transaction/StepRequestsWaterfallModal.tsx";
import EditStepForm from "@/legacy/transaction/transaction_script_editor/EditStepForm.jsx";
import IfStepForm from "@/legacy/transaction/transaction_script_editor/IfStepForm.jsx";
import StepTitleInput from "@/legacy/transaction/transaction_script_editor/StepTitleInput.jsx";
import TransactionUtils from "@/legacy/transaction/TransactionUtils.jsx";
import Utils from "@/legacy/Utils.js";

export default class StepsListItem extends React.Component {
  constructor(props) {
    super(props);
    Utils.autoBindClass(this);
    this.listItem = createRef();
    this.state = {
      showHelp: false,
    };
  }

  scrollIntoView() {
    this.listItem.current.scrollIntoView();
  }

  handleSelect(e) {
    if (e.target.className === "download-link" || e.target.parentElement.className === "download-link") {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (this.props.dropdownIndex === this.props.index) {
      this.props.onShowDropdown(this.props.index, false);
    }
    const scroll = this.props.onSelectStep(this.props.index);
    if (scroll) {
      setTimeout(this.scrollIntoView.bind(this), 0);
    }
  }

  handleDelete(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.onDeleteStep(this.props.index);
  }

  handleMenuClick(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDragStart(index, e) {
    index = index.toString();
    e.dataTransfer.effectAllowed = "move";

    try {
      e.dataTransfer.setData("application/json", index);
    } catch (exc) {
      e.dataTransfer.setData("text", index);
    }
  }

  handleDragOver(e) {
    e.preventDefault();
  }

  handleDrop(index, e) {
    e.preventDefault();
    var sourceIndex = null;

    try {
      sourceIndex = e.dataTransfer.getData("application/json");
    } catch (exc) {
      sourceIndex = e.dataTransfer.getData("text");
    }

    this.props.onReorderStep(parseInt(sourceIndex), index);
  }

  handleShowMoreClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({showHelp: !this.state.showHelp});
  }

  handleUpgradeDeprecatedClick(e) {
    e.preventDefault();
    e.stopPropagation();
    var step = this.props.step;
    this.props.onReplaceStep(this.props.index, step.replace_with);
  }

  handleAddStepBelow(e) {
    e.preventDefault();
    e.stopPropagation();
    var el = e.currentTarget;
    var button = el.parentElement.parentElement.parentElement;
    this.props.handleAddStart(e, button, this.props.index + 1);
  }

  getFirstSelector() {
    const step = this.props.step;
    for (let i = 0; i < step.fields.length; i++) {
      const field = step.fields[i];
      if (field.type === "SELECTOR") {
        return step.values[field.name];
      }
    }
    return null;
  }

  handleCopySelector(e) {
    e.preventDefault();
    e.stopPropagation();
    const selector = this.getFirstSelector();
    Utils.setClipboard(selector);
  }

  handleHtmlDownload(e) {
    e.preventDefault();
    e.stopPropagation();
    var step = this.props.step;
    var html = step.results.html;
    Utils.downloadTextAsFile("transaction-dom.html", html);
  }

  handleRawHtmlDownload(e) {
    e.preventDefault();
    e.stopPropagation();
    var step = this.props.step;
    var html = step.results.raw_html;
    Utils.downloadTextAsFile("transaction-raw.html", html);
  }

  handleViewScreenshot(e) {
    e.preventDefault();
    e.stopPropagation();
    var data = this.props.step.results.error_screenshot;
    Utils.openBase64Image(data);
  }

  hasDetails() {
    const empty = isEmpty(this.props.step.results.details);
    const originalResult = !!this.props.step.results.details;
    if (empty) {
      // no details
      return !empty;
    }
    // catch edge cases
    // { "parser": "json", "value": "[]" };
    if (get(this.props.step.results.details, "parser", "") === "json") {
      try {
        const emptyValue = isEmpty(JSON.parse(get(this.props.step.results.details, "value", [])));
        // no details
        return !emptyValue;
      } catch {
        return originalResult;
      }
    }

    return originalResult;
  }

  hasWaterfall() {
    return this.props.step.requests && this.props.step.requests.length > 0;
  }

  hasHtml() {
    return !!this.props.step.results.raw_html;
  }

  hasRawHtml() {
    return !!this.props.step.results.raw_html;
  }

  hasScreenshot() {
    return !!this.props.step.results.error_screenshot;
  }

  getDropdownItem = (icon, text, onClick) => (
    <Dropdown.Item onClick={onClick}>
      <div className="d-flex align-items-center">
        <Icon icon={icon} className="me-1" />
        {text}
      </div>
    </Dropdown.Item>
  );

  getStepIconButton(tooltip, icon, onClick) {
    return (
      <TooltipWrapper toolTip={tooltip} placement="bottom">
        <IconButton icon={icon} onClick={onClick} variant="ghost" size="fitIcon" iconProps={{size: "kpi"}} />
      </TooltipWrapper>
    );
  }

  render() {
    const step = this.props.step;

    return (
      <li
        ref={this.listItem}
        className={classNames(step.step_type, {
          selected: this.props.selected,
          error: step.results.error,
          success: !step.results.error && step.results.response_time >= 0,
        })}
      >
        {this.renderHeader()}
        {this.props.selected && this.renderForm()}
      </li>
    );
  }

  renderHeader() {
    const step = this.props.step;

    return (
      <div
        className="step-row p-2 d-flex align-items-center justify-content-between"
        onClick={this.handleSelect}
        draggable={this.props.allowMove}
        onDragStart={this.handleDragStart.bind(null, this.props.index)}
        onDragOver={this.handleDragOver}
        onDrop={this.handleDrop.bind(null, this.props.index)}
      >
        <div className="num pulseTextBodyBold pulse-border-radius-sm text-center flex-shrink-0">
          {this.props.index + 1}
        </div>
        <div className={classNames("description", step.step_type, "pulseTextBodyBold")}>
          <a
            dangerouslySetInnerHTML={{
              __html: TransactionUtils.formatStepDescription(step, this.props.shortSelectors),
            }}
          />
          {step.results.error ? <div className="error">{step.results.error}</div> : ""}
        </div>
        <div className="d-flex align-items-center step-details-icons">
          {this.hasDetails() && (
            <StepDetailsModalButton text="Show Details" icon={FiInfo} stepDetails={this.props.step?.results?.details} />
          )}
          {this.hasWaterfall() && (
            <StepRequestsWaterfallModal requests={this.props.step.requests} step={this.props.index + 1} />
          )}
          {this.hasRawHtml() &&
            this.getStepIconButton(
              "Download Raw HTML (before browser rendering)",
              FiFileText,
              this.handleRawHtmlDownload
            )}
          {this.hasHtml() &&
            this.getStepIconButton("Download HTML DOM (after browser rendering)", FiCode, this.handleHtmlDownload)}
          {this.hasScreenshot() && this.getStepIconButton("View Screenshot", FiCamera, this.handleViewScreenshot)}
          {step.results.error && (
            <TooltipWrapper toolTip="Has Error" placement="bottom">
              <Icon icon={FiAlertTriangle} size="kpi" />
            </TooltipWrapper>
          )}
        </div>
        {!step.results.error && step.results.response_time >= 0 && (
          <Chip variant="new" children={step.results.response_time + "ms"} />
        )}
        <div className="handle d-flex align-items-center">
          <Dropdown
            show={this.props.dropdownIndex === this.props.index}
            onToggle={(show, {originalEvent}) => {
              originalEvent.stopPropagation();
              this.props.onShowDropdown(this.props.index, show);
            }}
          >
            <Dropdown.Toggle variant="ghost" size="fitContent">
              <Icon icon={FiMoreVertical} size="kpi" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {this.hasDetails() && (
                <StepDetailsModalDropdownIcon
                  icon={FiInfo}
                  text="Show Details"
                  stepDetails={this.props.step?.results?.details}
                />
              )}
              {this.hasRawHtml() && this.getDropdownItem(FiFileText, "Download Raw HTML", this.handleRawHtmlDownload)}
              {this.hasHtml() && this.getDropdownItem(FiCode, "Download HTML DOM", this.handleHtmlDownload)}
              {this.hasScreenshot() && this.getDropdownItem(FiCamera, "View Screenshot", this.handleViewScreenshot)}
              {(this.hasDetails() || this.hasRawHtml() || this.hasHtml() || this.hasScreenshot()) && (
                <Dropdown.Divider />
              )}
              {this.getFirstSelector() !== null &&
                this.getDropdownItem(FiCopy, "Copy Element Selector", this.handleCopySelector)}
              {this.getDropdownItem(FiTrash, "Delete Step", this.handleDelete)}
              <Dropdown.Divider />
              {this.getDropdownItem(FiPlus, "Add Step Below", this.handleAddStepBelow)}
            </Dropdown.Menu>
          </Dropdown>
          <Icon icon={HiMenuAlt4} size="kpi" />
        </div>
      </div>
    );
  }

  renderForm() {
    const step = this.props.step;

    if (step.step_def === "C_IF") {
      return (
        <Card className="step-form if-step-form">
          <div
            className="pulseTextBody pulse-color-gray-600 mb-3"
            dangerouslySetInnerHTML={{__html: this.props.helpText}}
          />
          <StepTitleInput
            step={step}
            monitoringServiceType={this.props.monitoringServiceType}
            onChange={this.props.onUpdateSelectedStepTitle}
          />
          <IfStepForm
            step={step}
            index={this.props.index}
            transactionStepDefs={this.props.transactionStepDefs}
            transactionStepDefsMap={this.props.transactionStepDefsMap}
            onUpdateStepValues={this.props.onUpdateStepValues}
            htmlElementList={this.props.htmlElementList}
            onSelectElement={this.props.onSelectElement}
            onSelectElementCompleted={this.props.onSelectElementCompleted}
            monitoringServiceType={this.props.monitoringServiceType}
            onOpenURL={this.props.onOpenURL}
            enabledFeatureFlags={this.props.enabledFeatureFlags}
          />
        </Card>
      );
    }

    return (
      <Card className="step-form">
        {step.is_deprecated && step.replace_with && (
          <Alert
            callout="This step type has been deprecated"
            children={
              <span>
                <a onClick={this.handleUpgradeDeprecatedClick} href="#">
                  Click here
                </a>{" "}
                to automatically upgrade this step to its newer version.
              </span>
            }
            variant="maintenance"
          />
        )}
        {this.props.helpText.length < 250 ? (
          <div
            className={classNames("pulseTextBody pulse-color-gray-600", {"mb-3": step.fields.length > 0})}
            dangerouslySetInnerHTML={{__html: this.props.helpText}}
          />
        ) : (
          <div
            className={classNames("pulseTextBody pulse-color-gray-600", {
              "mb-3": step.fields.length > 0,
              "d-flex flex-row": !this.state.showHelp,
            })}
          >
            <div
              className={classNames(this.state.showHelp ? "mb-2" : "collapsed")}
              dangerouslySetInnerHTML={{__html: this.props.helpText}}
            />
            {!this.state.showHelp && <span className="me-2">...</span>}
            <a className={classNames("pulse-color-gray-800 text-nowrap")} href="#" onClick={this.handleShowMoreClick}>
              {this.state.showHelp ? "Show less" : "Learn more"}
            </a>
          </div>
        )}
        <StepTitleInput
          step={step}
          monitoringServiceType={this.props.monitoringServiceType}
          onChange={this.props.onUpdateSelectedStepTitle}
        />
        <EditStepForm
          step={this.props.step}
          htmlElementList={this.props.htmlElementList}
          monitoringServiceType={this.props.monitoringServiceType}
          onSelectElement={this.props.onSelectElement}
          onSelectElementCompleted={this.props.onSelectElementCompleted}
          onUpdateSelectedStepField={this.props.onUpdateSelectedStepField}
          onOpenURL={this.props.onOpenURL}
          enabledFeatureFlags={this.props.enabledFeatureFlags}
        />
      </Card>
    );
  }
}

StepsListItem.defaultProps = {
  index: 0,
  step: {},
  selected: false,
};
